import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import type { ListKind } from "shared";
import { LIST_FIELDS, type FieldDef } from "./fields";

type Item = Record<string, unknown>;

const isOff = (f: FieldDef, r: Item) => !!f.enabledWhen && String(r[f.enabledWhen.key] ?? "") !== f.enabledWhen.value;
const unlisted = (f: FieldDef, v: unknown) => String(v ?? "") !== "" && !(f.options ?? []).some((o) => o.value === String(v));

/** kind names the list (input ids and default columns); fields overrides the columns, e.g. a problem's linked programs. */
export function ListEditor({ kind, items, onSave, fields: custom }: {
  kind: ListKind | "programs" | "rules" | "income"; items: Item[]; onSave: (items: Item[]) => Promise<unknown>; fields?: FieldDef[];
}) {
  const fields = custom ?? LIST_FIELDS[kind as ListKind];
  const blank = () => Object.fromEntries(fields.map((f) => [f.key, f.options?.[0]?.value ?? ""]));
  const fromItems = () => items.map((i) => Object.fromEntries(fields.map((f) => [f.key, i[f.key] ?? ""])));
  const [rows, setRows] = useState<Item[]>(fromItems);
  const dirty = useRef(false);
  // New data from the server replaces the rows only when the user has no unsaved edits.
  useEffect(() => { if (!dirty.current) setRows(fromItems()); }, [JSON.stringify(items)]); // eslint-disable-line react-hooks/exhaustive-deps
  const [status, setStatus] = useState<{ error: string; saved: boolean; busy: boolean }>({ error: "", saved: false, busy: false });
  const edit = (next: Item[]) => { dirty.current = true; setRows(next); setStatus((s) => ({ ...s, saved: false, error: "" })); };
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    edit(next);
  };

  const save = async () => {
    const empty = (r: Item, f: FieldDef) => String(r[f.key] ?? "").trim() === "";
    // A row without a value that must be chosen (a linked program) is never dropped silently: saving waits for it.
    const blocked = rows.map((r, i) => {
      const f = fields.find((x) => x.required && x.missing && empty(r, x));
      return f ? `Row ${i + 1}: ${f.missing}, or remove the row.` : "";
    }).find(Boolean);
    if (blocked) { setStatus({ error: blocked, saved: false, busy: false }); return; }
    const clean = rows
      .filter((r) => fields.every((f) => !f.required || !empty(r, f)))
      .map((r) => Object.fromEntries(fields.map((f) => [f.key, isOff(f, r) ? null : String(r[f.key] ?? "").trim() || null])));
    setStatus({ error: "", saved: false, busy: true });
    try { await onSave(clean); dirty.current = false; setStatus({ error: "", saved: true, busy: false }); }
    catch (e) { setStatus({ error: (e as Error).message, saved: false, busy: false }); }
  };

  return (
    <div className="flex flex-col gap-2">
      {rows.map((r, i) => (
        <div key={i} className="flex items-start gap-2 rounded-md border border-slate-200 p-2 dark:border-slate-800">
          <div className="grid flex-1 gap-2 sm:grid-cols-2">
            {fields.map((f) => {
              const id = `${kind}-${i}-${f.key}`;
              const onChange = (e: { target: { value: string } }) => edit(rows.map((x, k) => (k === i ? { ...x, [f.key]: e.target.value } : x)));
              return (
                <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                  <label className="label" htmlFor={id}>{f.label}</label>
                  {f.type === "textarea" ? <textarea id={id} className="input" value={String(r[f.key] ?? "")} onChange={onChange} />
                    : f.type === "select" ? (
                      <select id={id} className="input" value={String(r[f.key] ?? "")} onChange={onChange}>
                        {/* A stored value with no option would show the first option's label while keeping the real value. */}
                        {unlisted(f, r[f.key]) && <option value={String(r[f.key])}>{String(r[f.key])} (not in the list)</option>}
                        {f.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : <input id={id} className="input" disabled={isOff(f, r)} value={String(r[f.key] ?? "")} onChange={onChange} />}
                </div>
              );
            })}
          </div>
          <div className="flex flex-col">
            <button type="button" className="btn-ghost px-1" aria-label="Move up" onClick={() => move(i, -1)}><ArrowUp size={14} /></button>
            <button type="button" className="btn-ghost px-1" aria-label="Move down" onClick={() => move(i, 1)}><ArrowDown size={14} /></button>
            <button type="button" className="btn-ghost px-1 text-red-600" aria-label="Remove" onClick={() => edit(rows.filter((_, k) => k !== i))}><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <button type="button" className="btn-ghost" onClick={() => edit([...rows, blank()])}><Plus size={14} /> Add</button>
        <button type="button" className="btn-primary" onClick={save} disabled={status.busy}>Save list</button>
        {status.saved && <span className="text-sm text-emerald-700">Saved</span>}
        {status.error && <span role="alert" className="text-sm text-red-600">{status.error}</span>}
      </div>
    </div>
  );
}
